import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Route, Routes, useSearchParams } from 'react-router-dom';
import {
  accountDeletionErrorMessage,
  confirmAccountDeletionRequest,
  submitAccountDeletionRequest,
} from './accountDeletion';
import emblem from './assets/brandfieldcrew-emblem.png';
import {
  contactErrorMessage,
  loadContactPolicies,
  submitContactLead,
  type ConsentPolicyConfiguration,
} from './contact';
import { readPublicEnvironment } from './environment';
import type { AndroidArchitecture, AndroidRelease } from './release';
import { TurnstileWidget } from './TurnstileWidget';
import { useAndroidRelease } from './useAndroidRelease';

const appUrl = 'https://app.brandfieldcrew.com';

function ArrowIcon() {
  return <span aria-hidden="true">↗</span>;
}

function Brand() {
  return (
    <Link className="brand" to="/" aria-label="Brand Field Crew home">
      <img src={emblem} alt="" />
      <span>Brand Field Crew</span>
    </Link>
  );
}

function SiteHeader() {
  return (
    <header className="site-header">
      <Brand />
      <nav aria-label="Main navigation">
        <a href="/#platform">Platform</a>
        <a href="/#organisations">For organisations</a>
        <a href="/#ambassadors">For BAs</a>
        <Link to="/download">Download</Link>
        <Link to="/contact">Contact</Link>
      </nav>
      <a className="button button-small button-light" href={appUrl}>Sign in <ArrowIcon /></a>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div>
        <Brand />
        <p>Clearer field operations for campaigns across Pakistan.</p>
      </div>
      <div className="footer-links">
        <div>
          <strong>Product</strong>
          <a href="/#platform">Platform</a>
          <Link to="/download">Android app</Link>
          <Link to="/contact">Contact BFC</Link>
          <a href={appUrl}>Operations sign in</a>
        </div>
        <div>
          <strong>Legal</strong>
          <a href={`${appUrl}/legal/terms`}>Terms of Service</a>
          <a href={`${appUrl}/legal/privacy`}>Privacy Notice</a>
          <Link to="/account-deletion">Account deletion</Link>
        </div>
      </div>
      <small>© 2026 Brand Field Crew. Pakistan.</small>
    </footer>
  );
}

function DashboardPreview() {
  return (
    <div className="dashboard-preview" aria-label="Illustration of Brand Field Crew operational reporting">
      <div className="preview-bar"><span /><span /><span /></div>
      <div className="preview-body">
        <aside>
          <img src={emblem} alt="" />
          <i className="active" /><i /><i /><i />
        </aside>
        <div className="preview-content">
          <div className="preview-heading"><span /><i /></div>
          <div className="preview-stats"><i /><i /><i /></div>
          <div className="preview-chart"><span /><span /><span /><span /><span /><span /></div>
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <>
      <SiteHeader />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Field operations, made visible</p>
            <h1>Every shift.<br />Every sale.<br /><em>One clear view.</em></h1>
            <p className="hero-intro">Plan campaigns, coordinate Brand Ambassadors, verify attendance and understand field performance across Pakistan.</p>
            <div className="hero-actions">
              <a className="button button-gold" href="#organisations">Explore the platform <ArrowIcon /></a>
              <Link className="text-link" to="/download">Download the BA app <span aria-hidden="true">→</span></Link>
            </div>
            <ul className="trust-list" aria-label="Platform highlights">
              <li><span>✓</span> Verified talent pool</li>
              <li><span>✓</span> Location-backed attendance</li>
              <li><span>✓</span> Reviewed sales reporting</li>
            </ul>
          </div>
          <div className="hero-visual"><DashboardPreview /></div>
        </section>

        <section className="proof-strip" aria-label="Brand Field Crew workflow">
          <div><strong>01</strong><span>Plan campaigns</span></div>
          <div><strong>02</strong><span>Staff every store</span></div>
          <div><strong>03</strong><span>Verify the field</span></div>
          <div><strong>04</strong><span>Review results</span></div>
        </section>

        <section className="section platform-section" id="platform">
          <div className="section-heading">
            <p className="eyebrow">Built for real field work</p>
            <h2>From campaign brief to verified result.</h2>
            <p>BFC keeps the operational chain connected without exposing one organisation's data to another.</p>
          </div>
          <div className="feature-grid">
            <article><span>01</span><h3>Campaign operations</h3><p>Connect clients, authorised agencies, stores, products, schedules and assignments in one tenant-safe workflow.</p></article>
            <article><span>02</span><h3>Attendance evidence</h3><p>Capture a live selfie and current GPS at check-in and checkout, with clear review signals for supervisors.</p></article>
            <article><span>03</span><h3>Daily sales</h3><p>Combine BA submissions into reviewable daily reports with controlled corrections and immutable decisions.</p></article>
            <article><span>04</span><h3>Operational insight</h3><p>Understand attendance, targets, store performance, product mix and BA outcomes from authorised data.</p></article>
          </div>
        </section>

        <section className="journey-section" id="organisations">
          <div className="journey-copy">
            <p className="eyebrow">For agencies and client teams</p>
            <h2>Run the field without losing the detail.</h2>
            <p>Build reusable store operations, assign the right people and review the evidence your organisation is authorised to see.</p>
            <ul>
              <li><b>Campaign setup</b><span>Stores, schedules, products and targets</span></li>
              <li><b>Staffing</b><span>Verified BAs and schedule-level offers</span></li>
              <li><b>Review</b><span>Attendance exceptions and daily sales decisions</span></li>
              <li><b>Reporting</b><span>Role-scoped dashboards and exports</span></li>
            </ul>
            <a className="button button-dark" href={appUrl}>Open operations dashboard <ArrowIcon /></a>
          </div>
          <div className="journey-panel">
            <p>One authorised view</p>
            <strong>Campaign</strong>
            <div className="flow-line"><span>Stores</span><i /><span>Schedules</span><i /><span>People</span></div>
            <div className="result-card"><small>Field result</small><b>Attendance + sales</b><span>Ready for review</span></div>
          </div>
        </section>

        <section className="section ambassador-section" id="ambassadors">
          <div className="phone-frame" aria-hidden="true">
            <div className="phone-top" />
            <img src={emblem} alt="" />
            <small>MY WORK</small>
            <h3>Good morning,<br />Ayesha.</h3>
            <div className="shift-card"><span>Current shift</span><b>Liberty Demo Counter</b><small>10:00 am–6:00 pm</small><i>Check in</i></div>
          </div>
          <div className="ambassador-copy">
            <p className="eyebrow">For Brand Ambassadors</p>
            <h2>Your workday in your pocket.</h2>
            <p>See accepted assignments, record attendance and submit product sales from one focused Android application—even when connectivity is unreliable.</p>
            <div className="mini-features"><span>Offline-safe submissions</span><span>Personal work history</span><span>Secure profile controls</span></div>
            <Link className="button button-gold" to="/download">Get the Android app <span aria-hidden="true">↓</span></Link>
          </div>
        </section>

        <section className="final-cta">
          <p className="eyebrow">Controlled Production pilot</p>
          <h2>Ready to make field work clearer?</h2>
          <p>Brand Field Crew is onboarding selected agencies, clients and Brand Ambassadors in Pakistan.</p>
          <div><Link className="button button-gold" to="/contact">Discuss a pilot <ArrowIcon /></Link><a className="button button-outline" href={appUrl}>Sign in to BFC</a><Link className="button button-outline" to="/download">Download Android app</Link></div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function ArchitectureCard({ architecture, androidRelease }: { architecture: AndroidArchitecture; androidRelease: AndroidRelease }) {
  const release = androidRelease.architectures[architecture];
  const recommended = architecture === 'arm64';
  return (
    <article className={`download-card${recommended ? ' recommended' : ''}`}>
      {recommended && <span className="recommendation">Recommended</span>}
      <h2>{release.label}</h2>
      <p>{release.detail}</p>
      <dl>
        <div><dt>Version</dt><dd>{androidRelease.version}</dd></div>
        <div><dt>Size</dt><dd>{release.size}</dd></div>
        <div><dt>CPU</dt><dd>{architecture === 'arm64' ? 'arm64-v8a' : 'armeabi-v7a'}</dd></div>
      </dl>
      <a className={`button ${recommended ? 'button-gold' : 'button-dark'}`} href={release.url}>Download APK <span aria-hidden="true">↓</span></a>
      <details><summary>Verify checksum</summary><code>{release.sha256}</code></details>
    </article>
  );
}

function DownloadPage() {
  const { release: androidRelease, status } = useAndroidRelease();
  return (
    <>
      <SiteHeader />
      <main className="download-page">
        <section className="download-hero">
          <p className="eyebrow">Official Android release</p>
          <h1>Take Brand Field Crew into the field.</h1>
          <p>Download the signed Production app directly from BFC's protected Cloudflare distribution domain.</p>
          <div className="release-meta"><span>Version {androidRelease.version}</span><span>Released {androidRelease.releasedOn}</span><span>{androidRelease.minimumAndroid} or newer</span></div>
          {status === 'loading' ? <p className="release-status" role="status">Checking for the latest verified release…</p> : null}
          {status === 'fallback' ? <p className="release-status release-status-warning" role="alert">Live release details are temporarily unavailable. Showing the last verified release.</p> : null}
        </section>
        <section className="download-grid" aria-label="Android downloads">
          <ArchitectureCard architecture="arm64" androidRelease={androidRelease} />
          <ArchitectureCard architecture="armv7" androidRelease={androidRelease} />
        </section>
        <section className="install-guide">
          <div>
            <p className="eyebrow">Installation guide</p>
            <h2>Install safely in four steps.</h2>
          </div>
          <ol>
            <li><span>1</span><p><b>Choose the recommended APK</b>Use the older build only if ARM64 will not install.</p></li>
            <li><span>2</span><p><b>Open the completed download</b>Your browser may ask permission to install unknown apps.</p></li>
            <li><span>3</span><p><b>Confirm the BFC publisher</b>Android may show a first-install Play Protect notice for direct distribution.</p></li>
            <li><span>4</span><p><b>Sign in to Production</b>The app connects only to <code>api.brandfieldcrew.com</code>.</p></li>
          </ol>
        </section>
        <aside className="security-note">
          <strong>Only download BFC from this page.</strong>
          <p>Official APKs are versioned, cryptographically signed and served over HTTPS from <code>downloads.brandfieldcrew.com</code>. Never install a file shared through an unknown link.</p>
        </aside>
      </main>
      <SiteFooter />
    </>
  );
}

function ContactPage() {
  const [policies, setPolicies] = useState<ConsentPolicyConfiguration | null>(null);
  const [policyError, setPolicyError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const submissionId = useRef(crypto.randomUUID());
  const submissionInFlight = useRef(false);
  const turnstileSiteKey = readPublicEnvironment('VITE_TURNSTILE_SITE_KEY');

  useEffect(() => {
    let active = true;
    void loadContactPolicies()
      .then((configuration) => {
        if (active) setPolicies(configuration);
      })
      .catch(() => {
        if (active) setPolicyError(true);
      });
    return () => { active = false; };
  }, []);

  const acceptTurnstile = useCallback((token: string) => {
    setTurnstileToken(token);
    setResult(null);
  }, []);
  const clearTurnstile = useCallback(() => setTurnstileToken(''), []);

  function resetTurnstile() {
    setTurnstileToken('');
    setTurnstileReset((current) => current + 1);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!policies || !turnstileToken || submissionInFlight.current) return;

    const fields = new FormData(form);
    const value = (name: string) => {
      const field = fields.get(name);
      return typeof field === 'string' ? field.trim() : '';
    };
    submissionInFlight.current = true;
    setSubmitting(true);
    setResult(null);
    try {
      const response = await submitContactLead({
        submission_id: submissionId.current,
        full_name: value('full_name'),
        work_email: value('work_email'),
        organisation_name: value('organisation_name'),
        phone: value('phone'),
        organisation_type: value('organisation_type'),
        enquiry_type: value('enquiry_type'),
        message: value('message'),
        privacy_accepted: fields.has('privacy_accepted'),
        privacy_version: policies.privacy.version,
        marketing_consent: fields.has('marketing_consent'),
        marketing_version: policies.marketing.version,
        turnstile_token: turnstileToken,
        website: value('website'),
      });
      form.reset();
      submissionId.current = crypto.randomUUID();
      setResult({ kind: 'success', message: response.detail });
    } catch (error) {
      setResult({ kind: 'error', message: contactErrorMessage(error) });
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
      resetTurnstile();
    }
  }

  let readiness = 'Complete the secure verification to send your enquiry.';
  if (!policies && !policyError) readiness = 'Loading the current Privacy Notice…';
  else if (policyError) readiness = 'The current Privacy Notice could not be loaded. Refresh and try again.';
  else if (!turnstileSiteKey) readiness = 'Secure submission is not configured for this environment.';
  else if (submitting) readiness = 'Sending your enquiry securely…';
  else if (turnstileToken) readiness = 'Secure verification complete. Your enquiry is ready to send.';

  return (
    <>
      <SiteHeader />
      <main className="contact-page">
        <section className="contact-intro">
          <div>
            <p className="eyebrow">Start a conversation</p>
            <h1>Bring clarity to your next field campaign.</h1>
            <p>Tell us what your agency or client team needs. BFC is currently onboarding selected organisations for its controlled Pakistan pilot.</p>
          </div>
          <div className="contact-promises" aria-label="What happens next">
            <article><span>01</span><div><strong>Share your requirement</strong><p>Campaign scale, locations and the operational problem you want to solve.</p></div></article>
            <article><span>02</span><div><strong>BFC reviews the fit</strong><p>We assess whether the controlled pilot supports your current workflow.</p></div></article>
            <article><span>03</span><div><strong>Plan a safe onboarding</strong><p>Approved organisations follow the platform-controlled onboarding process.</p></div></article>
          </div>
        </section>

        <section className="contact-form-section" aria-labelledby="contact-form-heading">
          <form className="contact-form" onSubmit={(event) => { void submit(event); }}>
            <div className="contact-form-heading">
              <p className="eyebrow">Pilot enquiry</p>
              <h2 id="contact-form-heading">Tell us about your organisation.</h2>
              <p>Fields marked required must be completed before an enquiry can be submitted.</p>
            </div>

            <div className="contact-fields">
              <label>
                <span>Full name <b aria-hidden="true">*</b></span>
                <input autoComplete="name" maxLength={120} name="full_name" required type="text" />
              </label>
              <label>
                <span>Work email <b aria-hidden="true">*</b></span>
                <input autoComplete="email" maxLength={254} name="work_email" required type="email" />
              </label>
              <label>
                <span>Organisation name <b aria-hidden="true">*</b></span>
                <input autoComplete="organization" maxLength={160} name="organisation_name" required type="text" />
              </label>
              <label>
                <span>Phone number <small>(optional)</small></span>
                <input autoComplete="tel" inputMode="tel" maxLength={32} name="phone" type="tel" />
              </label>
              <label>
                <span>Organisation type <b aria-hidden="true">*</b></span>
                <select defaultValue="" name="organisation_type" required>
                  <option disabled value="">Select organisation type</option>
                  <option value="agency">Agency</option>
                  <option value="client">Client brand</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                <span>Enquiry type <b aria-hidden="true">*</b></span>
                <select defaultValue="" name="enquiry_type" required>
                  <option disabled value="">Select enquiry type</option>
                  <option value="pilot">Join the BFC pilot</option>
                  <option value="demo">Request a product demonstration</option>
                  <option value="partnership">Discuss a partnership</option>
                  <option value="support">Get product support</option>
                  <option value="other">Other enquiry</option>
                </select>
              </label>
              <label className="contact-message-field">
                <span>How can BFC help? <b aria-hidden="true">*</b></span>
                <textarea maxLength={2000} minLength={10} name="message" required rows={7} />
                <small>Do not include CNICs, passwords, private media links or other sensitive information.</small>
              </label>
              <label aria-hidden="true" className="contact-honeypot">
                <span>Website</span>
                <input autoComplete="off" name="website" tabIndex={-1} type="text" />
              </label>
            </div>

            <fieldset className="contact-consent">
              <legend>Privacy and communication</legend>
              <label>
                <input name="privacy_accepted" required type="checkbox" />
                <span>I have read the <a href={policies?.privacy.url ?? `${appUrl}/legal/privacy`} rel="noreferrer" target="_blank">Privacy Notice</a> and agree that BFC may use these details to respond to my enquiry. <b>Required</b></span>
              </label>
              <label>
                <input name="marketing_consent" type="checkbox" />
                <span>I would also like to receive occasional BFC product and pilot updates. <small>Optional; this does not affect the enquiry.</small></span>
              </label>
            </fieldset>

            {turnstileSiteKey ? (
              <TurnstileWidget
                action="contact_lead"
                key={turnstileReset}
                onUnavailable={clearTurnstile}
                onVerify={acceptTurnstile}
                siteKey={turnstileSiteKey}
              />
            ) : null}

            {result ? <p className={`contact-result contact-result-${result.kind}`} role={result.kind === 'error' ? 'alert' : 'status'}>{result.message}</p> : null}

            <div className="contact-submit-row">
              <p id="contact-readiness" role="status"><strong>Protected contact form</strong>{readiness}</p>
              <button
                aria-describedby="contact-readiness"
                className="button button-dark"
                disabled={!policies || !turnstileToken || submitting}
                type="submit"
              >{submitting ? 'Sending…' : 'Send enquiry'} {!submitting && <ArrowIcon />}</button>
            </div>
          </form>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function AccountDeletionPage() {
  const [searchParams] = useSearchParams();
  const verificationToken = searchParams.get('verification_token')?.trim() ?? '';
  const [policies, setPolicies] = useState<ConsentPolicyConfiguration | null>(null);
  const [policyError, setPolicyError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const submissionId = useRef(crypto.randomUUID());
  const submissionInFlight = useRef(false);
  const turnstileSiteKey = readPublicEnvironment('VITE_TURNSTILE_SITE_KEY');

  useEffect(() => {
    if (verificationToken) return undefined;
    let active = true;
    void loadContactPolicies()
      .then((configuration) => { if (active) setPolicies(configuration); })
      .catch(() => { if (active) setPolicyError(true); });
    return () => { active = false; };
  }, [verificationToken]);

  const acceptTurnstile = useCallback((token: string) => {
    setTurnstileToken(token);
    setResult(null);
  }, []);
  const clearTurnstile = useCallback(() => setTurnstileToken(''), []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!policies || !turnstileToken || submissionInFlight.current) return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    const value = (name: string) => {
      const field = fields.get(name);
      return typeof field === 'string' ? field.trim() : '';
    };
    submissionInFlight.current = true;
    setSubmitting(true);
    setResult(null);
    try {
      const response = await submitAccountDeletionRequest({
        submission_id: submissionId.current,
        email: value('email'),
        privacy_acknowledged: fields.has('privacy_acknowledged'),
        privacy_version: policies.privacy.version,
        turnstile_token: turnstileToken,
        website: value('website'),
      });
      form.reset();
      submissionId.current = crypto.randomUUID();
      setResult({ kind: 'success', message: response.detail });
    } catch (error) {
      setResult({ kind: 'error', message: accountDeletionErrorMessage(error) });
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
      setTurnstileToken('');
      setTurnstileReset((current) => current + 1);
    }
  }

  async function confirm() {
    if (!verificationToken || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setSubmitting(true);
    setResult(null);
    try {
      const response = await confirmAccountDeletionRequest(verificationToken);
      setResult({ kind: 'success', message: response.detail });
    } catch (error) {
      setResult({ kind: 'error', message: accountDeletionErrorMessage(error) });
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  }

  let readiness = 'Complete secure verification before submitting the request.';
  if (!policies && !policyError) readiness = 'Loading the current Privacy Notice…';
  else if (policyError) readiness = 'The current Privacy Notice could not be loaded. Refresh and try again.';
  else if (!turnstileSiteKey) readiness = 'Secure submission is not configured for this environment.';
  else if (submitting) readiness = 'Submitting your request securely…';
  else if (turnstileToken) readiness = 'Secure verification complete. Your request is ready.';

  return (
    <>
      <SiteHeader />
      <main className="deletion-page">
        <section className="deletion-hero">
          <p className="eyebrow">Privacy request</p>
          <h1>Request account deletion.</h1>
          <p>Use this page to ask Brand Field Crew to delete your account and associated personal data. No sign-in is required.</p>
        </section>
        <section className="deletion-form-section">
          {verificationToken ? (
            <div className="contact-form deletion-confirmation">
              <div className="contact-form-heading">
                <p className="eyebrow">Verify your request</p>
                <h2>Submit this account deletion request?</h2>
                <p>Verification places the request into review. It does not immediately deactivate the account or erase operational records.</p>
              </div>
              <div className="deletion-retention">
                <strong>What may be retained</strong>
                <p>BFC may retain limited security, audit, legal, dispute, and campaign-accountability records where required. Access is restricted and retained data is no longer used for ordinary account activity.</p>
              </div>
              {result ? <p className={`contact-result contact-result-${result.kind}`} role={result.kind === 'error' ? 'alert' : 'status'}>{result.message}</p> : null}
              {!result || result.kind === 'error' ? <button className="button button-dark" disabled={submitting} onClick={() => { void confirm(); }} type="button">{submitting ? 'Submitting…' : 'Verify and submit request'}</button> : null}
            </div>
          ) : (
            <form className="contact-form" onSubmit={(event) => { void submit(event); }}>
              <div className="contact-form-heading">
                <p className="eyebrow">Account and associated data</p>
                <h2>Start a verified deletion request.</h2>
                <p>Enter the email address used for your BFC account. For privacy, the response is the same whether or not an account matches.</p>
              </div>
              <div className="contact-fields deletion-fields">
                <label>
                  <span>Account email <b aria-hidden="true">*</b></span>
                  <input autoComplete="email" maxLength={254} name="email" required type="email" />
                </label>
                <label aria-hidden="true" className="contact-honeypot">
                  <span>Website</span><input autoComplete="off" name="website" tabIndex={-1} type="text" />
                </label>
              </div>
              <div className="deletion-retention">
                <strong>How the request works</strong>
                <ol>
                  <li>We send a one-time verification link to the account email.</li>
                  <li>After verification, BFC reviews the account and applicable retention obligations.</li>
                  <li>We confirm the outcome by email. Operational records required for security, audit, legal, or dispute handling may be retained with restricted access.</li>
                </ol>
              </div>
              <fieldset className="contact-consent">
                <legend>Privacy acknowledgement</legend>
                <label>
                  <input name="privacy_acknowledged" required type="checkbox" />
                  <span>I have read the <a href={policies?.privacy.url ?? `${appUrl}/legal/privacy`} rel="noreferrer" target="_blank">Privacy Notice</a> and understand that BFC must verify and review this request. <b>Required</b></span>
                </label>
              </fieldset>
              {turnstileSiteKey ? <TurnstileWidget action="account_deletion" key={turnstileReset} onUnavailable={clearTurnstile} onVerify={acceptTurnstile} siteKey={turnstileSiteKey} /> : null}
              {result ? <p className={`contact-result contact-result-${result.kind}`} role={result.kind === 'error' ? 'alert' : 'status'}>{result.message}</p> : null}
              <div className="contact-submit-row">
                <p id="deletion-readiness" role="status"><strong>Protected deletion request</strong>{readiness}</p>
                <button aria-describedby="deletion-readiness" className="button button-dark" disabled={!policies || !turnstileToken || submitting} type="submit">{submitting ? 'Submitting…' : 'Request account deletion'} {!submitting && <ArrowIcon />}</button>
              </div>
            </form>
          )}
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function NotFoundPage() {
  return (
    <main className="not-found">
      <Brand />
      <p className="eyebrow">404</p>
      <h1>This page is not in the field.</h1>
      <Link className="button button-gold" to="/">Return home</Link>
    </main>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
